import { Buffer } from "buffer";
import { EventEmitter } from "events";
import axios, { AxiosInstance } from "axios";

import { DEFAULT_DATABASE_NAME, DEFAULT_DATABASE_VERSION, DEFAULT_ERROR_TILE_URL, DEFAULT_REFETCH_AGE_DAYS, DEFAULT_TILE_OBJECT_STORE_NAME, DEFAULT_UI_TITLE_OP_INPROGRESS } from "../../../../ServiceWorker/TileCache/indexedDb/constants";
import { ITileCoordinates } from "../../tileCoordinates";
import { IIndexedDbTileCacheEntry, IIndexedDbTileCacheOptions } from "./types";
import { Logic } from "./logic";
import { ITileCache, ITileCacheInfo } from "../types";
import { IndexedDbLogic } from "./indexedDbLogic";


/**
 * Class for a spatial-tile-cache that stores its data in the browsers IndexedDB
 */
export class IndexedDbTileCache extends EventEmitter implements ITileCache {

    constructor(
        private readonly options: IIndexedDbTileCacheOptions = {}
    ) {
        super();

        this.options.databaseName = this.options.databaseName || DEFAULT_DATABASE_NAME;
        this.options.tileObjectStoreName = this.options.tileObjectStoreName || DEFAULT_TILE_OBJECT_STORE_NAME;
        this.options.errorTileUrl = this.options.errorTileUrl || DEFAULT_ERROR_TILE_URL;
        this.options.tileRefetchAgeDays = this.options.tileRefetchAgeDays || DEFAULT_REFETCH_AGE_DAYS;
        this.options.uiTitleOpInProgress = this.options.uiTitleOpInProgress || DEFAULT_UI_TITLE_OP_INPROGRESS;
    }

    /**
     * Receives a tile as its base64 encoded data url.
     */
    public getTileAsDataUrl(tileCoordinates: ITileCoordinates, urlTemplate: string, urlSubdomains?: string[]): Promise<string> {
        return this.getTileEntry(tileCoordinates, urlTemplate, urlSubdomains, true).then((tileEntry: IIndexedDbTileCacheEntry | null) => {

            if (!!tileEntry) {
                const buffer = Buffer.from(tileEntry.data);
                return Promise.resolve("data:" + tileEntry.contentType + ";base64," + buffer.toString('base64'));
            }
            else {
                return Promise.resolve(this.options.errorTileUrl!);
            }
        });
    }

    /**
     * Get the internal tile entry from the database with all its additional meta information.
     * If the tile is marked as outdated by the `IIndexedDbTileCacheOptions.maxAge` property, it tries to download it again. On any error it will provide the cached version.
     * If you pass `true` as parameter for the `fetchIfCacheMiss` argument, it tries to dowenload a tile if it is not stored already.
     */
    private getTileEntry(
        tileCoordinates: ITileCoordinates,
        urlTemplate: string, urlSubdomains?: string[],
        fetchIfCacheMiss?: boolean
    ): Promise<IIndexedDbTileCacheEntry | null> {

        return new Promise((resolve, reject) => {

            const dbRequest = IndexedDbLogic.openDatabase(this.options.databaseName!, DEFAULT_DATABASE_VERSION, reject, this);
            const client = axios.create();

            dbRequest.addEventListener("success", (dbEvent: any) => {
                const database: IDBDatabase = dbEvent.target.result;

                const query = database.transaction([this.options.tileObjectStoreName!])
                    .objectStore(this.options.tileObjectStoreName!).get(Logic.createInternalTileUrl(urlTemplate, tileCoordinates));

                query.addEventListener("success", (event: any) => {
                    if (!event.target.result) {
                        if (fetchIfCacheMiss) {
                            return this.downloadTile(tileCoordinates, client, urlTemplate, urlSubdomains).then(resolve, reject);
                        }
                        return reject(new Error("Unable to find entry"));
                    }

                    const tileEntry: IIndexedDbTileCacheEntry = event.target.result as IIndexedDbTileCacheEntry;

                    const refetchCutoffTimestamp = Logic.subtractDaysFromTimestamp(Date.now(), this.options.tileRefetchAgeDays);

                    if (tileEntry.timestamp < refetchCutoffTimestamp) { // Too old, try to refetch
                        return this.downloadTile(tileCoordinates, client, urlTemplate, urlSubdomains).catch(() => {
                            // Not available so keep cached version...
                            return resolve(tileEntry as IIndexedDbTileCacheEntry);
                        }).then((dlResult) => resolve(dlResult!));
                    }

                    resolve(tileEntry);
                });

                query.addEventListener("error", (event: any) => {
                    this.emit("error", dbEvent.target.error);
                    reject(event.target.error);
                });

            });

        });
    }

    /**
     * Download a specific tile by its coordinates and store it within the indexed-db
     */
    private async downloadTile(tileCoordinates: ITileCoordinates, client: AxiosInstance, urlTemplate: string, urlSubdomains?: string[]): Promise<IIndexedDbTileCacheEntry | null> {

        return new Promise(async (resolve, reject) => {
            
            try {
                const response = await client.get(Logic.createTileUrl(urlTemplate, urlSubdomains ?? [], tileCoordinates), { responseType: "arraybuffer" });

                const contentType = response.headers["content-type"] as string;
                const url = Logic.createInternalTileUrl(urlTemplate, tileCoordinates);

                const cacheEntry = await Logic.cacheTileFetchResult(this.options, contentType, response.data, url);

                resolve(cacheEntry);
            } 
            catch (error: any) {
                resolve(null);
            }
        });
    }

    /**
     * Purge the whole store
     */
    public purgeStore(): Promise<boolean> {

        return new Promise((resolve, reject) => {

            const dbRequest = IndexedDbLogic.openDatabase(this.options.databaseName!, DEFAULT_DATABASE_VERSION, reject, this);

            dbRequest.addEventListener("success", (dbEvent: any) => {
                const database: IDBDatabase = dbEvent.target.result;

                const query = database.transaction([this.options.tileObjectStoreName!], "readwrite")
                    .objectStore(this.options.tileObjectStoreName!).clear();

                query.addEventListener("success", (_event) => {
                    resolve(true);
                });

                query.addEventListener("error", (event: any) => {
                    this.emit("error", dbEvent.target.error);
                    reject(event.target.error);
                });
            });

        });

    }

    /**
     * Get information about the cache, eg. storage size.
     */
    public getInfo(): Promise<ITileCacheInfo> {

        return new Promise((resolve, reject) => {

            const dbRequest = IndexedDbLogic.openDatabase(this.options.databaseName!, DEFAULT_DATABASE_VERSION, reject, this);

            dbRequest.addEventListener("success", (dbEvent: any) => {
                const database: IDBDatabase = dbEvent.target.result;

                let cumlSizeBytes = 0;
                let cumlNumTiles = 0;

                const cursorRequest = database.transaction([this.options.tileObjectStoreName!], "readonly")
                    .objectStore(this.options.tileObjectStoreName!)
                    .openCursor();

                cursorRequest.addEventListener("success", () => {
                    let cursor = cursorRequest.result;

                    if (cursor) {
                        cumlSizeBytes += cursor.value.data.byteLength;
                        ++cumlNumTiles;

                        cursor.continue();
                    }
                    else {
                        resolve({
                            storageSizeBytes: cumlSizeBytes,
                            numberOfTiles: cumlNumTiles
                        });
                    }
                });

                cursorRequest.addEventListener("error", (event: any) => {
                    reject(event.target.error);
                });
            });

        });

    }
}